import { apiRequest } from './http';
import type { ApiItemResponse, ApiListResponse, FlowDryRunResult, FlowExecutionRecord, FlowRecord } from '../types/api';

type Query = Record<string, string | number | boolean | undefined>;

function queryString(query: Query = {}): string {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export const flowsApi = {
  list: (query: Query) => apiRequest<ApiListResponse<FlowRecord>>(`/flows${queryString(query)}`),
  get: async (id: string) => (await apiRequest<ApiItemResponse<FlowRecord>>(`/flows/${id}`)).data,
  create: async (payload: Record<string, unknown>) =>
    (await apiRequest<ApiItemResponse<FlowRecord>>('/flows', { method: 'POST', body: JSON.stringify(payload) })).data,
  createVersion: async (flowId: string, payload: Record<string, unknown>) =>
    (await apiRequest<ApiItemResponse<FlowRecord>>(`/flows/${flowId}/versions`, { method: 'POST', body: JSON.stringify(payload) })).data,
  approve: async (flowId: string, versionId: string) =>
    (await apiRequest<ApiItemResponse<FlowRecord>>(`/flows/${flowId}/versions/${versionId}/approve`, { method: 'POST', body: JSON.stringify({}) })).data,
  activate: async (flowId: string, versionId: string) =>
    (await apiRequest<ApiItemResponse<FlowRecord>>(`/flows/${flowId}/versions/${versionId}/activate`, { method: 'POST', body: JSON.stringify({}) })).data,
  rollback: async (flowId: string, versionId: string) =>
    (await apiRequest<ApiItemResponse<FlowRecord>>(`/flows/${flowId}/versions/${versionId}/rollback`, { method: 'POST', body: JSON.stringify({}) })).data,
  execute: async (flowId: string, payload: Record<string, unknown>) =>
    (await apiRequest<ApiItemResponse<FlowExecutionRecord>>(`/flows/${flowId}/executions`, { method: 'POST', body: JSON.stringify(payload) })).data,
  dryRun: async (flowId: string, payload: { sourcePayload: unknown; mappingVersionId?: string }) =>
    (await apiRequest<FlowDryRunResult>(`/flows/${flowId}/dry-run`, { method: 'POST', body: JSON.stringify(payload) })),
  executions: (query: Query) => apiRequest<ApiListResponse<FlowExecutionRecord>>(`/flow-executions${queryString(query)}`),
  execution: async (id: string) => (await apiRequest<ApiItemResponse<FlowExecutionRecord>>(`/flow-executions/${id}`)).data,
  replay: async (executionId: string, reason: string) =>
    (await apiRequest<ApiItemResponse<{ duplicate: boolean; replay: { id: string; status: string } }>>(
      `/flow-executions/${executionId}/replay`,
      { method: 'POST', body: JSON.stringify({ mode: 'REPLAY_ORIGINAL', reason }) },
    )).data,
};
